import React from "react";
import {
  ActionIcon,
  Divider,
  Group,
  Stack,
  Switch,
  Text,
  Tooltip,
} from "@mantine/core";
import { IconInfoCircle } from "@tabler/icons-react";
import ScheduleAdjustments from "../ScheduleAdjustments";
import type { DayHours, DayKey, WeeklyHours } from "../../../types/staffForm";
import { DAYS } from "./staffFormUtils";
import { WeeklyHoursEditor } from "./WeeklyHoursEditor";

interface HoursPanelProps {
  hasBookableCalendar: boolean;
  onBookableChange: (checked: boolean) => void;
  hours: WeeklyHours;
  onToggleDay: (day: DayKey, checked: boolean) => void;
  onTimeChange: (day: DayKey, side: keyof DayHours, value: string) => void;
  /** Present when editing; schedule adjustments need a saved staff member. */
  staffId?: string | null;
  businessLocationId?: string;
}

/**
 * Hours: calendar access first, then — only for a bookable calendar — the
 * weekly schedule and its one-off adjustments.
 */
export function HoursPanel({
  hasBookableCalendar,
  onBookableChange,
  hours,
  onToggleDay,
  onTimeChange,
  staffId,
  businessLocationId,
}: HoursPanelProps) {
  return (
    <Stack gap="lg">
      <Switch
        size="md"
        color="teal"
        checked={hasBookableCalendar}
        onChange={(event) => onBookableChange(event.currentTarget.checked)}
        label={
          <Group gap={6} wrap="nowrap">
            <Text size="sm">Has a bookable calendar</Text>
            <Tooltip
              label="If enabled, this user will contribute towards the bookable calendar limits of your plan"
              withArrow
              multiline
              w={260}
              events={{ hover: true, focus: true, touch: true }}
            >
              <ActionIcon
                variant="transparent"
                size="xs"
                color="gray"
                aria-label="More info about the bookable calendar setting"
                // Stop the click from toggling the Switch label.
                onClick={(event) => event.preventDefault()}
              >
                <IconInfoCircle size={16} />
              </ActionIcon>
            </Tooltip>
          </Group>
        }
      />

      {hasBookableCalendar ? (
        <>
          <Divider label="Weekly Hours" labelPosition="left" />
          <WeeklyHoursEditor
            hours={hours}
            onToggleDay={onToggleDay}
            onTimeChange={onTimeChange}
          />

          {/* Ad-hoc one-off hours that overwrite the weekly schedule above. Edit
            mode only: an override needs a real staffId. */}
          {staffId && (
            <ScheduleAdjustments
              staffId={staffId}
              businessLocationUuid={businessLocationId}
              weeklySchedule={DAYS.map(({ key }) => {
                const start = hours[key].start || null;
                const end = hours[key].end || null;
                return { isWorking: !!(start && end), start, end };
              })}
            />
          )}
        </>
      ) : (
        // Hours only matter for a bookable calendar; they stay stored (and
        // untouched) while it's off.
        <Text size="sm" c="dimmed">
          Turn on a bookable calendar to set weekly hours and schedule
          adjustments.
        </Text>
      )}
    </Stack>
  );
}
